/**
 * RoadmapCommentReplyRow
 *
 * Single reply row rendered under a RoadmapCommentRow's children list.
 *
 * Structural sibling of the content CommentReplyRow, swapping CommentEntity for
 * RoadmapCommentEntity. Two roadmap divergences (both verified against Flutter
 * roadmap_comment_widget.dart):
 *   - NO like button — Flutter `:446` renders a Container() for replies
 *     ("do not allow likes on content replies"). Only parent comments get a like.
 *   - NO media thumbnail — roadmap comments carry no mediaInfo.
 * Renders the " > {replyToName}" reply-target link exactly like the content
 * sibling (Flutter `:216-225`): "You" when the reply target is the current user.
 */

import React from 'react';
import { useSelector } from 'react-redux';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { CachedImage } from '@/presentation/components/common/CachedImage';
import { UserUtils } from '@/domain/utils/userUtils';
import { useDateFormatters } from '@/presentation/utils/useDateFormatters';
import { Navigator } from '@/presentation/routing/Navigator';
import type { RootState } from '@/presentation/store';
import { renderMentionText } from '@/presentation/feature/content/components/sheets/comments/components/utils/renderMentionText';
import { qaAttr } from '@/presentation/utils/qaAttr';
import type { RoadmapCommentEntity } from '@/domain/entities/roadmapComment/roadmapCommentEntity';

export interface RoadmapCommentReplyRowProps {
  reply: RoadmapCommentEntity;
  highlighted: boolean;
  onReply: (comment: RoadmapCommentEntity) => void;
}

export const RoadmapCommentReplyRow: React.FC<RoadmapCommentReplyRowProps> = ({
  reply,
  highlighted,
  onReply,
}) => {
  const { theme } = useTheme();
  const responsive = useResponsiveDesign();
  const { l10n } = useLocale();
  const dateFormatters = useDateFormatters();
  const currentUser = useSelector((state: RootState) => state.auth.user);

  const isCurrentUser = currentUser?.firebaseId === reply.userId;
  const displayName = isCurrentUser ? l10n.comments.you : reply.userName;
  const replyTarget = reply.commentToReply;
  const replyToName = replyTarget
    ? currentUser?.firebaseId === replyTarget.userId
      ? l10n.comments.you
      : replyTarget.userName
    : null;

  return (
    <div
      {...qaAttr({ id: `roadmap-comment-reply-${reply.id}` })}
      style={{
        display: 'flex',
        gap: responsive.gap.base,
        paddingTop: responsive.spacing.sm,
        paddingBottom: responsive.spacing.sm,
        backgroundColor: highlighted ? theme.backgroundSecondary : 'transparent',
      }}
    >
      <div
        onClick={() => Navigator.navigateToUserDetails(reply.userId)}
        style={{ cursor: 'pointer', flexShrink: 0 }}
      >
        <CachedImage
          src={UserUtils.generateUserProfileImageUrl(reply.userId)}
          alt={reply.userName}
          style={{
            width: responsive.avatar.small,
            height: responsive.avatar.small,
            borderRadius: responsive.borderRadius.round,
            objectFit: 'cover',
          }}
        />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: responsive.gap.xs }}>
          <span
            onClick={() => Navigator.navigateToUserDetails(reply.userId)}
            style={{
              fontWeight: responsive.fontWeight.semibold,
              color: theme.text.primary,
              fontSize: responsive.font.sm,
              cursor: 'pointer',
            }}
          >
            {displayName}
          </span>
          {replyTarget && replyToName && (
            <>
              <span
                style={{
                  color: theme.text.secondary,
                  fontSize: responsive.font.sm,
                }}
              >
                {'>'}
              </span>
              <span
                onClick={() => Navigator.navigateToUserDetails(replyTarget.userId)}
                style={{
                  fontWeight: responsive.fontWeight.semibold,
                  color: theme.text.primary,
                  fontSize: responsive.font.sm,
                  cursor: 'pointer',
                }}
              >
                {replyToName}
              </span>
            </>
          )}
        </div>
        <div
          style={{
            color: theme.text.primary,
            fontSize: responsive.font.sm,
            marginTop: responsive.spacing.xxs,
            wordBreak: 'break-word',
          }}
        >
          {renderMentionText(reply.text, theme.exclusive)}
        </div>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: responsive.gap.base,
            marginTop: responsive.spacing.xs,
          }}
        >
          <span style={{ fontSize: responsive.font.xs, color: theme.text.secondary }}>
            {dateFormatters.formatDate(reply.createdTimestamp)}
          </span>
          <button
            onClick={() => onReply(reply)}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: theme.text.primary,
              fontSize: responsive.font.xs,
              fontWeight: responsive.fontWeight.semibold,
              padding: 0,
            }}
          >
            {l10n.comments.reply}
          </button>
        </div>
      </div>
    </div>
  );
};
